import { Box, Stack, Typography, Tooltip, Button, CircularProgress } from '@mui/material';
import { ArrowRight2, SearchNormal1, TickCircle } from 'iconsax-reactjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomTable from 'components/@extended/CustomTable';
import ExtIconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import SkeletonTable from 'components/SkeletonTable';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { getBatchStudents, releaseResults } from 'services/evalution.service';
import TextField from '@mui/material/TextField';
import Snackbar from 'utils/Snackbar';
import useAuth from 'hooks/useAuth';
import Chip from '@mui/material/Chip';

interface StudentRow {
  id: string;
  name: string;
  email: string;
  marksByPc: any;
  evaluationResult?: string | null;
}

function studentEvalDisplayStatus(
  row: StudentRow,
  opts: { resultsReleased: boolean; sessionEnd: Date | null }
): { label: string; color: 'success' | 'error' | 'warning' } {
  const r = row.evaluationResult;
  if (opts.resultsReleased || r === 'Pass' || r === 'Fail') {
    return { label: 'Completed', color: 'success' };
  }
  const end = opts.sessionEnd;
  if (end && end.getTime() < Date.now()) {
    return { label: 'Overdue', color: 'error' };
  }
  return { label: 'Pending', color: 'warning' };
}

const BatchStudentsMarks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const batchId = (location.state as any)?.batchId;
  
  const assessmentSessionId = (location.state as any)?.assessmentSessionId;

  const [batchInfo, setBatchInfo] = useState<any>(null);
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [inputValue, setInputValue] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sort, setSort] = useState<Array<{ id: string; sort?: boolean; desc?: boolean }>>([]);
  const [total, setTotal] = useState(0);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [initialTableReady, setInitialTableReady] = useState(false);

  /**
   * Build marksByPc from batch session NOS → PCs → questions.
   * For reassessment, `reassessmentPcIds` limits to PCs in NOS the student failed on the previous attempt
   * (full question paper is still configured for the session; evaluation UI is scoped per student).
   */
  const buildMarksStructure = (data: any, reassessmentPcIds?: string[]) => {
    const structure: any = {};
    const allow =
      reassessmentPcIds != null && reassessmentPcIds.length > 0 ? new Set(reassessmentPcIds.map(String)) : null;

    (data?.nos || []).forEach((nos: any) => {
      (nos?.pcs || []).forEach((pc: any) => {
        const pcKey = String(pc.pcId);
        if (allow && !allow.has(pcKey)) return;

        const questions: any[] = [];

        // Support both flat array and legacy { theory: [], practical: [] } shape
        const rawQuestions: any[] = Array.isArray(pc?.questions)
          ? pc.questions
          : [...(pc?.questions?.theory || []), ...(pc?.questions?.practical || [])];

        rawQuestions.forEach((q: any) => {
          questions.push({
            qId: q.questionId,
            questionText: q.question,
            maxMarks: q.marks,
            marks: '',
            rubricId: typeof q.rubric === 'object' && q.rubric !== null ? (q.rubric as any)._id ?? '' : q.rubric ?? ''
          });
        });

        structure[pc.pcId] = {
          pcId: pc.pcId,
          pcCode: pc.pcCode,
          pcName: pc.pcName,
          maxMarks: pc.totalMarks,
          questions
        };
      });
    });

    return structure;
  };

  const handleSearchInputChange = useCallback((value: string) => {
    setInputValue(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearch(value);
      setPageIndex(1);
    }, 500);
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    setInitialTableReady(false);
  }, [batchId, assessmentSessionId]);

  // 🔹 Fetch Batch Students
  useEffect(() => {
    const fetchData = async () => {
      if (!batchId) return;

      setLoading(true);
      const activeSortItem = sort?.[0];
      const sortColumn = activeSortItem?.id;
      const sortOrder = activeSortItem ? (activeSortItem.desc ? 'desc' : 'asc') : undefined;
      const query: any = {
        page: pageIndex,
        limit: pageSize,
        search: search || undefined,
        sortColumn,
        sortOrder
      };
      try {
        const res: any = await getBatchStudents(batchId, assessmentSessionId, query);
        const data = res?.data?.data || res?.data;

        setBatchInfo(data);

        const sessionType = data?.sessionType;
        const approvedStudents: StudentRow[] = (data?.students || [])
          .filter((s: any) => s.status === 'approve')
          .map((s: any) => {
            const pcFilter =
              sessionType === 'reassessment' &&
              Array.isArray(s.reassessmentPcIds) &&
              s.reassessmentPcIds.length > 0
                ? s.reassessmentPcIds
                : undefined;
            return {
              id: s.user?._id,
              name: `${s.user?.firstName || ''} ${s.user?.lastName || ''}`.trim(),
              email: s.user?.email,
              evaluationResult: s.evaluationResult ?? null,
              marksByPc: buildMarksStructure(data, pcFilter)
            };
          });

        setStudents(approvedStudents);
        setTotal(data?.total ?? approvedStudents.length);
      } catch (err) {
        console.error('Error fetching students:', err);
      } finally {
        setLoading(false);
        setInitialTableReady(true);
      }
    };

    fetchData();
  }, [batchId, assessmentSessionId, pageIndex, pageSize, search, sort]);

  const resultsReleased = !!batchInfo?.resultsReleased;
  const evaluatedCount = batchInfo?.evaluatedCount ?? 0;
  const totalStudents = batchInfo?.total ?? total;
  const allAssessed = totalStudents > 0 && evaluatedCount >= totalStudents;
  const canRelease = !resultsReleased && allAssessed;
  const assignedAssessorId = batchInfo?.assignedAssessorId != null ? String(batchInfo.assignedAssessorId) : '';
  const isAssessorAdmin = user?.type === 'assessorAdmin';
  const currentUserId = String(user?._id ?? user?.id ?? '');
  const isOwnSession = Boolean(currentUserId && assignedAssessorId && assignedAssessorId === currentUserId);
  /** Only the session assignee may release (API enforces this too). */
  const mayReleaseResults = canRelease && isOwnSession;
  const sessionEndDate = batchInfo?.sessionEndDate ? new Date(batchInfo.sessionEndDate) : null;

  const handleReleaseResults = useCallback(async () => {
    if (!batchId || !assessmentSessionId || !mayReleaseResults) return;
    setReleaseLoading(true);
    try {
      await releaseResults(batchId, assessmentSessionId);
      Snackbar('Results released successfully. Marks can no longer be edited.', 'success');
      const res: any = await getBatchStudents(batchId, assessmentSessionId, {
        page: pageIndex,
        limit: pageSize,
        search: search || undefined,
        sortColumn: sort?.[0]?.id,
        sortOrder: sort?.[0] ? (sort[0].desc ? 'desc' : 'asc') : undefined
      });
      const data = res?.data?.data || res?.data;
      setBatchInfo(data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to release results.';
      Snackbar(msg, 'error');
    } finally {
      setReleaseLoading(false);
    }
  }, [batchId, assessmentSessionId, mayReleaseResults, pageIndex, pageSize, search, sort]);

  // 🔹 Table Columns
  const columns = useMemo(
    () => [
      {
        id: 'name',
        header: 'STUDENT NAME',
        accessorKey: 'name'
      },
      {
        id: 'email',
        header: 'EMAIL',
        accessorKey: 'email'
      },
      ...(isAssessorAdmin
        ? [
            {
              id: 'evalStatus',
              header: 'STATUS',
              enableSorting: false,
              cell: ({ row }: any) => {
                const { label, color } = studentEvalDisplayStatus(row.original, {
                  resultsReleased,
                  sessionEnd: sessionEndDate && !Number.isNaN(sessionEndDate.getTime()) ? sessionEndDate : null
                });
                return <Chip size="small" label={label} color={color} variant="outlined" sx={{ fontWeight: 600 }} />;
              }
            }
          ]
        : []),
      {
        id: 'actions',
        header: 'OPTIONS',
        enableSorting: false,
        cell: ({ row }: any) => {
          const viewOnlyMarks = resultsReleased || !isOwnSession;
          return (
            <Tooltip
              title={
                !isOwnSession
                  ? 'Only the assessor assigned to this batch/session can enter marks.'
                  : resultsReleased
                    ? 'Results already released — view marks'
                    : 'Enter marks'
              }
            >
              <span>
                <ExtIconButton
                  color="primary"
                  disabled={!isOwnSession}
                  onClick={() =>
                    navigate('/performance-evaluation/student-marks-entry', {
                      state: {
                        student: row.original,
                        batchName: batchInfo?.batchName || '',
                        batchInfo: { ...batchInfo, resultsReleased },
                        assessmentSessionId,
                        studentId: row.original.id,
                        batchId,
                        viewOnly: viewOnlyMarks,
                         sessionType : batchInfo?.sessionType
                      }
                    })
                  }
                >
                  <ArrowRight2 />
                </ExtIconButton>
              </span>
            </Tooltip>
          );
        }
      }
    ],
    [navigate, batchInfo, resultsReleased, assessmentSessionId, batchId, isAssessorAdmin, isOwnSession, sessionEndDate]
  );

  const handleSortingData = useCallback((payload: { name: string }) => {
    setSort((prev) => {
      const existing = prev.find((s) => s.id === payload.name);
      if (!existing) {
        return [{ id: payload.name, sort: true, desc: false }];
      }
      if (existing.sort && !existing.desc) {
        return [{ id: payload.name, sort: true, desc: true }];
      }
      if (existing.sort && existing.desc) {
        return [];
      }
      return prev;
    });
    setPageIndex(1);
  }, []);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Header */}
      <MainCard
        title={
          <Stack direction="row" alignItems="center" spacing={2}>
            <BackToggle show />
            <Box>
              <Typography variant="h5">{batchInfo?.batchName || 'Batch'} - Students</Typography>
              <Typography variant="caption">Enter marks for approved students</Typography>
            </Box>
          </Stack>
        }
        content={false}
        secondary={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 2 }}>
            <Tooltip
              title={
                resultsReleased
                  ? 'Results already released'
                  : !isOwnSession
                    ? 'Only the assessor assigned to this batch/session can release results'
                    : !allAssessed
                      ? `Assess all students first (${evaluatedCount}/${totalStudents} assessed)`
                      : 'Release results so that marks can no longer be edited'
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!mayReleaseResults || releaseLoading}
                  startIcon={releaseLoading ? <CircularProgress size={16} color="inherit" /> : <TickCircle size={18} />}
                  onClick={handleReleaseResults}
                >
                  {releaseLoading ? 'Releasing...' : 'Release Result'}
                </Button>
              </span>
            </Tooltip>
            <TextField
              placeholder="Search by name or email..."
              size="small"
              value={inputValue}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setPageIndex(1);
              }}
            />
            <ExtIconButton variant="outlined" onClick={() => setPageIndex(1)}>
              <SearchNormal1 />
            </ExtIconButton>
          </Stack>
        }
      />

      {/* Table */}
      <MainCard content={false}>
        {!initialTableReady && loading ? (
          <SkeletonTable columns={columns} />
        ) : (
          <CustomTable
            columns={columns}
            data={students}
            sortArray={['name', 'email']}
            sort={sort}
            setSortingData={handleSortingData}
            striped
            count={total}
            size={pageSize}
            pageIndex={pageIndex}
            handlePageChange={(_event: unknown, page: number) => setPageIndex(page)}
            handleSizeChange={(e: any) => {
              setPageSize(Number(e.target.value));
              setPageIndex(1);
            }}
            notFound={total === 0 && !loading}
            search={search}
            loading={initialTableReady && loading}
          />
        )}
      </MainCard>
    </Box>
  );
};

export default BatchStudentsMarks;
